import { action, computed, observable } from 'mobx';

import { StringKeyMap } from '../adl-gen/common';
import { DbKey, WithDbId } from '../adl-gen/common/db';
import {
  AppUser,
  CompetencySkillTrainingOffering,
  I18nContent,
  LearningProvider,
  Quiz,
  Skill,
  TrainingOffering,
  TrainingOfferingCategory,
  UserQuiz,
  WorkRole,
  WorkRoleScoreRange
} from '../adl-gen/totalinteraction/futurefit/db';
import {
  ComparisonRoles,
  Competency,
  MultiUserQuizResults,
  QuizMapAndScores,
  Recommendation,
  ResultsScoreSet,
  RoleData,
  SingleUserQuizResults,
  SingleUserTrainingRecommendations,
  UserQuizResults
} from '../adl-gen/totalinteraction/futurefit/frontend';
import { SingleUserResults } from '../adl-gen/totalinteraction/futurefit/requests';
import { TranslateFn } from '../internationalization/i18n-hoc';
import { fromPromise, MaybeLoading } from '../loader/loader';
import { Service } from '../service/service';
import { LoadedProps } from '../ui/screens/quiz-results-screen/quiz-results-screen';
import { SelectOption } from '../ui/widgets/select/select';
import { average, compareByGet, objectIdsValues } from '../util/utils';

import { LoggedIn } from './identity-store';
import { isSubset, Settings } from './manager-quiz-results-store';

/// Common interface for QuizResultsHelper
export interface QuizReportResults {
  quizResults: UserQuizResults[];
  quizMapAndScores: QuizMapAndScores;
  usersCompleted: DbKey<AppUser>[];
}

export function multiUserQuizResultsToQuizReportResults(results: MultiUserQuizResults): QuizReportResults {
  return {
    quizResults: results.quizResults,
    quizMapAndScores: results.quizMapAndScores,
    usersCompleted: results.quizResults.map(r => r.appUserId)
  };
}

export function singleUserQuizResultsToQuizReportResults(results: SingleUserQuizResults): QuizReportResults {
  return {
    quizResults: [results.quizResults],
    quizMapAndScores: results.quizMapAndScores,
    usersCompleted: [results.quizResults.appUserId]
  };
}

// Type safe keys of datasets returned from the backend.
export interface Plot {
  actual: ResultsScoreSet;
  expected: ResultsScoreSet;
  previous: ResultsScoreSet | undefined;
  difference: ResultsScoreSet;
  industry: ResultsScoreSet;
  group: ResultsScoreSet;

}

export interface ResultsByAppUser {
  [key: string]: Plot & { fullname: string };
}

export interface BarCategories {
  apprentice: string;
  journeyman: string;
  master: string;
}

export const plotOrderKeys = {
  actual: 'actual',
  expected: 'expected',
  previous: 'previous',
  difference: 'difference',
  industry: 'industry',
  group: 'group'
};

/**
 * Extra data for complex handling of RoleRange recommendations:
 * (1) Suppress category altogether when Gap exceeds 100% - regardless of content
 * and
 * (2) Always show category when Gap < 100% - even if empty
 */

export type CategoryOption = SelectOption & { gap: number | undefined };

export type LearningType = 'recommendations' | 'all-learning';

/** Accessor helper to pull results out of SingleUserQuizResults | MultiUserQuizResults */

export type PlotType =
  | {
    kind: 'overall';
  }
  | {
    kind: 'competency';
    competencyId: DbKey<Competency>;
  }
  | {
    kind: 'skill';
    skillId: DbKey<Skill>;
  };
export class QuizResultsHelper {
  // Unpack Single|MultiUserQuizResults.quizMapAndScores.map
  private readonly competencies: QuizReportResults['quizMapAndScores']['map']['competencies'];
  private readonly skills: QuizReportResults['quizMapAndScores']['map']['skills'];
  private readonly skillsByCompetency: QuizReportResults['quizMapAndScores']['map']['skillsByCompetency'];
  private readonly usersCompleted: QuizReportResults['usersCompleted'];
  private readonly skillGroups: QuizReportResults['quizMapAndScores']['skillGroups'];

  private readonly results: ResultsByAppUser = {};
  private readonly roles: ComparisonRoles;

  private readonly providerIds?: DbKey<LearningProvider>[];

  readonly quizId: DbKey<Quiz>;
  readonly quiz: Quiz;
  readonly ranges: WorkRoleScoreRange[];

  @observable
  private selectedRoleId?: DbKey<WorkRole>;

  @observable
  private selectedPriorities?: LearningType[];

  private readonly plotOrder: (keyof Plot)[];

  private debugReporting: boolean = false;

  static fromQuizResultsWithProvider(
    quizResults: QuizReportResults,
    roles: ComparisonRoles,
    providerIds: DbKey<LearningProvider>[],
    selectedRoleId?: string
  ): QuizResultsHelper {
    return new QuizResultsHelper(
      quizResults,
      roles,
      selectedRoleId,
      providerIds
    );
  }

  static fromQuizReportResults(
    quizReportResults: QuizReportResults,
    roles: ComparisonRoles,
    selectedRoleId?: string
  ): QuizResultsHelper {
    return new QuizResultsHelper(quizReportResults, roles, selectedRoleId);
  }

  private constructor(
    /// We actually don't need to manage the SingleUserQuizResults version as this is never
    /// called with it - but - you can't overload constructors in Typescript
    private readonly quizReportResults: QuizReportResults,
    roles: ComparisonRoles,
    selectedRoleId?: string,
    providerIds?: DbKey<LearningProvider>[]
  ) {
    const { quizResults, quizMapAndScores, usersCompleted } = quizReportResults;

    this.competencies = quizMapAndScores.map.competencies;
    this.skills = quizMapAndScores.map.skills;
    this.skillsByCompetency = quizMapAndScores.map.skillsByCompetency;
    this.usersCompleted = usersCompleted;
    this.skillGroups = quizMapAndScores.skillGroups;

    // Expect all quizzes returned to be the same

    this.quizId = quizMapAndScores.quiz.id;
    this.quiz = quizMapAndScores.quiz.value;
    this.ranges = quizMapAndScores.ranges;

    this.selectedRoleId = selectedRoleId;

    const emptyResults: ResultsByAppUser = {};

    this.results = quizResults.reduce((map: ResultsByAppUser, result) => {
      map[result.appUserId] = {
        fullname: result.fullname,
        actual: result.datasets.actual,
        expected: quizMapAndScores.datasets.expected,
        previous: result.datasets.previous,
        difference: result.datasets.difference,
        group: quizMapAndScores.datasets.group,
        industry: quizMapAndScores.datasets.industry,
      };
      return map;
    }, emptyResults);

    this.plotOrder = Object.entries(plotOrderKeys)
      .filter(([key, _]) => key !== 'difference')
      .map(([plotkey, _]) => plotkey) as (keyof Plot)[];

    this.providerIds = providerIds;

    // Default to ensure existing quiz is present: This is necessary because otherwise we end up passing an empty role map
    // to RolesFilter
    const defaultRoleIntersection: ComparisonRoles = {
      roles: { [this.quiz.workRoleId]: roles.roles[this.quiz.workRoleId] }
    };
    const intersectedRoles: ComparisonRoles = Object.entries(roles.roles)
      .filter(([_, roleData]) =>

        // Ensure the set of skills in each role is present in the 'expected' set of skills in the first quiz result
        isSubset(
          new Set<string>(Object.keys(roleData.expected.bySkill)),
          new Set<string>(Object.keys(quizMapAndScores.datasets.expected.bySkill))
        )
      )
      .reduce((map: ComparisonRoles, [id, roleData]) => {
        map.roles[id] = roleData;
        return map;
      }, defaultRoleIntersection);
    // Filter Client WorkRoles: Default to remove 'global' roles entirely

    // Default to ensure existing quiz is present: This is necessary because otherwise we end up passing an empty role map
    // to RolesFilter
    const defaultClientRole: ComparisonRoles = { roles: { [this.quiz.workRoleId]: roles.roles[this.quiz.workRoleId] } };

    // The roles passed in by the initial load should be filtered on the user's client ID
    // on the server so filtering on the client ID should not be required here.

    this.roles = Object.entries(intersectedRoles.roles)
      .filter(
        ([_, roleData]) => roleData.workRole.value.owner.kind === 'appClientId'
      )
      // filter on global only
      .reduce((map: ComparisonRoles, [id, roleData]) => {
        map.roles[id] = roleData;
        return map;
      }, defaultClientRole);
  }

  /// FF2-426
  debugReports = (onOff: boolean = false) => {
    this.debugReporting = onOff;
  };
  isDebuggingReports = () => {
    return this.debugReporting;
  };

  /** Ratio of the actual to expected score */
  scoreRatioOfExpected(type: PlotType = { kind: 'overall' }): number | undefined {
    const actual = this.actual(type);
    const expected = this.expected(type);
    if (expected === 0) {
      return undefined;
    }
    return actual / expected;
  }

  /**
   * FF2-315
   * Special case: It's possible that, for the overall score, you can get "100% towards desired" even if
   * the participant clearly hasn't achieved 100% or above for each Competency. This is because the expected
   * score can equal the actual score overall when averaged out.
   * To combat this, in this case only, cap each skill at 100% before calculating the overall result.
   */
  scoreHeadlineRatio(): number | undefined {
    const cappedRatiosBySkill = this.getSkillIds().map(skillId => {
      const actualBySkill = this.score('actual', { kind: 'skill', skillId });
      const expectedBySkill = this.score('expected', { kind: 'skill', skillId });
      if (expectedBySkill === 0) {
        return undefined;
      }
      if (actualBySkill >= expectedBySkill) {
        return 1.0;
      } else {
        return actualBySkill / expectedBySkill;
      }
    });
    return average(cappedRatiosBySkill);
  }

  scoreHeadlineRatioText(): string {
    const scoreRatio = this.scoreHeadlineRatio();
    if (scoreRatio === undefined) {
      return 'N/A';
    }
    return `${Math.round(scoreRatio * 100)}`;
  }

  // TODO: Move this to the display layer
  scoreRatioOfExpectedText(type: PlotType = { kind: 'overall' }): string {
    /// FF2-426
    if (!this.debugReporting) {
      const scoreRatio = this.scoreRatioOfExpected(type);
      if (scoreRatio === undefined) {
        return 'N/A';
      }
      return `${Math.round(scoreRatio * 100)} %`;
    } else {
      const actual = this.actual(type);
      const expected = this.expected(type);
      if (expected === 0) {
        return `[actual ${Math.round(actual)}]`;
      } else {
        return `${Math.round(actual)}/${Math.round(expected)}`;
      }
    }
  }

  dataset(plot: keyof Plot): ResultsScoreSet[] {
    const raw = Object.values(this.results).map(results => results[plot]);
    // Some keys (like previous) aren't always populated
    // tslint:disable-next-line: strict-type-predicates
    return raw.filter(r => r !== undefined) as ResultsScoreSet[];
  }

  private getScoreFromType(type: PlotType): (t: ResultsScoreSet) => number | undefined {
    if (type.kind === 'overall') {
      return t => t.overall;
    } else if (type.kind === 'competency') {
      return t => t.byCompetency[type.competencyId];
    } else if (type.kind === 'skill') {
      return t => t.bySkill[type.skillId];
    }
    throw Error('Unimplemented property type');
  }

  score(plot: keyof Plot, type: PlotType): number {
    const scores = this.dataset(plot)
      .map(this.getScoreFromType(type))
      .filter(num => num !== undefined);
    return average(scores);
  }

  actual(type: PlotType = { kind: 'overall' }) {
    return this.score('actual', type);
  }

  expected(type: PlotType = { kind: 'overall' }) {
    return this.score('expected', type);
  }

  previous(type: PlotType = { kind: 'overall' }) {
    return this.score('previous', type);
  }

  difference(type: PlotType = { kind: 'overall' }) {
    return this.score('difference', type);
  }

  group(type: PlotType = { kind: 'overall' }) {
    return this.score('group', type);
  }

  industry(type: PlotType = { kind: 'overall' }) {
    return this.score('industry', type);
  }

  competency(competencyId: DbKey<Competency>) {
    return this.competencies[competencyId];
  }

  skill(skillId: DbKey<Skill>) {
    return this.skills[skillId];
  }

  getSkillIds() {
    return Object.keys(this.skills);
  }

  /// cf. competenciesWithDbId
  skillsWithDbId(xlat?: TranslateFn) {
    return objectIdsValues(this.skills).sort(compareByGet(t => (xlat ? xlat(t.value.name) : t.value.name)));
  }

  /// Get Skill Ids sorted
  getSkillKeys(xlat?: TranslateFn): DbKey<Skill>[] {
    return this.skillsWithDbId(xlat).map(sid => sid.id);
  }

  /// Get Skills sorted
  getSkills(xlat?: TranslateFn): Skill[] {
    return this.skillsWithDbId(xlat).map(sid => sid.value);
  }

  //  - needs to be arrow func to bind _this_ (or maybe slice() will work)
  private readonly getSkillsWithIds = (xlat?: TranslateFn): WithDbId<Skill>[] => {
    return this.skillsWithDbId(xlat);
  };

  /// FF2-407 as getSkillIds returns all mapped skills, this can be used to determine if there are
  /// really any actual scores against them
  actualScoreForSkillExists(skillId: DbKey<Skill>) {
    const actualScoresForSkill = this.dataset('actual')
      .map(this.getScoreFromType({ kind: 'skill', skillId }))
      .filter(num => num !== undefined);
    if (actualScoresForSkill.length === 0) {
      return false;
    }
    return true;
  }

  /// Use arrow => otherwise this may be unbound unless passed in
  getSkillsByCompetency = (competencyId: DbKey<Competency>) => {
    return this.skillsByCompetency[competencyId];
  };

  allUsers(): string[] {
    return Object.values(this.results).map(r => r.fullname);
  }

  getUsersCompleted() {
    return this.usersCompleted;
  }

  /// competencies with keys, sorted:
  private competenciesWithDbId(_xlat?: TranslateFn) {
    return objectIdsValues(this.competencies).sort(
      // compareByGet(t => xlat ? xlat(t.value.name) : t.value.name)
      compareByGet(t => t.value.ordering)
    );
  }

  /// competency keys sorted - needs to be arrow func to bind _this_
  getCompetencyKeys(xlat?: TranslateFn): DbKey<Competency>[] {
    return this.competenciesWithDbId(xlat).map(cid => cid.id);
  }

  /// competencies sorted
  getCompetencies(xlat?: TranslateFn): Competency[] {
    return this.competenciesWithDbId(xlat).map(cid => cid.value);
  }

  /// Turns out we need this too - sorted on name - needs to be arrow func to bind _this_
  ///  (or maybe slice() will work)
  private readonly getCompetenciesWithIds = (xlat?: TranslateFn): WithDbId<Competency>[] => {
    return this.competenciesWithDbId(xlat);
  };

  getAllRoles(): StringKeyMap<string, RoleData> {
    return this.roles.roles;
  }

  getOtherRoleIds(): string[] {
    return Object.keys(this.getAllRoles()).filter(id => id !== this.quiz.workRoleId);
  }

  getDefaultRole(): WithDbId<WorkRole> | undefined {
    return Object.values(this.roles.roles)
      .map(roleData => roleData.workRole)
      .find(role => role.id === this.quiz.workRoleId);
  }

  getSelectedRoleShortName(): I18nContent | undefined {
    return this.selectedRoleId ? this.roles.roles[this.selectedRoleId].workRole.value.shortname : undefined;
  }

  getSelectedRoleId(): DbKey<WorkRole> | undefined {
    return this.selectedRoleId;
  }

  @computed
  get selectedRoleResult(): {
    [key: string]: ResultsScoreSet | undefined;
  } {
    const roles = this.roles || { roles: {} };
    return {
      comparisonRole: this.selectedRoleId ? roles.roles[this.selectedRoleId].expected : undefined,
    };
  }

  getSelectedRole(): WorkRole | undefined {
    const roles = this.roles || { roles: {} };
    return this.selectedRoleId ? roles.roles[this.selectedRoleId].workRole.value : undefined;
  }

  isRoleSelected(): boolean {
    return !!this.getSelectedRoleId();
  }

  @action
  setSelectedRoleId = (roleId: string) => {
    this.selectedRoleId = roleId;
  };

  getSkillGroups(): StringKeyMap<string, DbKey<Skill>[]> | undefined {
    return this.skillGroups;
  }

  @action
  setPriorityFilter = (selected?: LearningType[]) => {
    if (selected && selected.length > 0) {
      this.selectedPriorities = selected;
      return;
    }
    this.selectedPriorities = undefined;
  };

  @computed
  get priorityFilter() {
    return this.selectedPriorities;
  }

  // TODO: The constructor of quizResultsStore now is required to perform a lot more processing
  // Change this to not call the constructor
  byAppUser(appUserId: string): QuizResultsHelper {
    const quizResults = this.quizReportResults.quizResults.filter(results => results.appUserId === appUserId);
    const usersCompleted = [appUserId];
    return QuizResultsHelper.fromQuizReportResults(
      { ...this.quizReportResults, quizResults, usersCompleted },
      this.roles
    );
  }

  // Role comparisons: Now, we no longer have the default role data because we've fetched the comparison role
  // instead so we aren't using it.
  getPlotKeys(): (keyof Plot)[] {
    if (this.isRoleSelected()) {
      const keys: (keyof Plot)[] = ['actual', 'expected'];
      this.setExpectedLabelToComparisonRole();
      return keys;
    }
    const keySet = new Set<keyof Plot>();
    for (const plot of Object.values(this.results)) {
      this.plotOrder
        .filter(k => plot[k] !== undefined)
        .filter(k => k !== 'expected' || !this.hideExpected())
        /// FF2-429 TODO This could be generalized but would need to store all plotkeys mapped to true/false on the quiz in the db
        .filter(k => k !== 'industry' || this.showIndustryAverage())
        .forEach(k => keySet.add(k));
    }
    return Array.from(keySet.keys());
  }

  getAppUsers(): string[] {
    return Object.keys(this.results);
  }

  plotExists(plotkey: keyof Plot): boolean {
    return this.getPlotKeys().includes(plotkey);
  }

  recommendationProviders(): DbKey<LearningProvider>[] | undefined {
    return this.providerIds;
  }

  // Write as arrow func to access "this"
  guidanceAvailable = (): boolean => {
    return this.quiz.guidanceAvailable;
  };

  // Write as arrow func to access "this"
  recommendationsAvailable = (): boolean => {
    return this.quiz.recommendationsAvailable;
  };

  showIndustryAverage = (): boolean => {
    return this.quiz.showIndustryAverage;
  };

  hideExpected = (): boolean => {
    return this.quiz.hideExpected;
  };

  getExpectedLabel(t: TranslateFn): string | undefined {
    return this.quiz.expectedLabel === null ? undefined : t(this.quiz.expectedLabel);
  }

  getActualLabel(t: TranslateFn): string | undefined {
    return this.quiz.actualLabel === null ? undefined : t(this.quiz.actualLabel);
  }

  setExpectedLabelToComparisonRole(): void {
    this.quiz.expectedLabel = this.getSelectedRoleShortName() || null;
  }

  getGroupLabel(t: TranslateFn): string | undefined {
    return this.quiz.groupLabel === null ? undefined : t(this.quiz.groupLabel);
  }

  getIndustryLabel(t: TranslateFn): string | undefined {
    return this.quiz.industryLabel === null ? undefined : t(this.quiz.industryLabel);
  }

  /// Tags are used to display badges or pills on Recommendation tiles.
  /// This function generates options based on Competency names. Although TrainingOfferings
  /// can be mapped to multiple Skills (and Competencies), each instance is mapped to a
  /// single Skill or Competency via the link tables SkillTrainingOffering or CompetencyTrainingOffering.
  ///
  /// The function works by extracting both Skills and Competencies. For Competencies the label field
  /// is the corresponding Competency name for the CompetencyId. For Skills, we find out the Skill's
  /// associated Competency and set the label to that name against the SkillId.
  ///
  /// Reusing SelectOption interface to provide these for convenience.
  getTagOptions = (t: TranslateFn): SelectOption[] => {
    /// Use Competencies (or skills) for tags - but only Competency names as labels
    const competenciesWithIds = this.getCompetenciesWithIds(t);
    const competencyNameMap: StringKeyMap<DbKey<Skill>, I18nContent> = this.getSkillsWithIds(t).reduce<
      StringKeyMap<DbKey<Skill>, I18nContent>
    >((map, next) => {
      const competencyWithId = competenciesWithIds.find(c => c.id === next.value.competencyId);
      if (competencyWithId) {
        map[next.id] = competencyWithId.value.fullName;
      }
      return map;
    }, {});
    return [
      ...this.getCompetenciesWithIds(t).map(c => ({ label: t(c.value.fullName), value: c.id })),
      ...this.getSkillsWithIds(t).map(s => ({ label: t(competencyNameMap[s.id]), value: s.id }))
    ];
  };

  /// Categories are used for section headings - tabs or accordions.
  /// This function generates options based on Competency names. Although TrainingOfferings
  /// can be mapped to multiple Skills (and Competencies), each instance is mapped to a
  /// single Skill or Competency via the link tables SkillTrainingOffering or CompetencyTrainingOffering.
  ///
  /// The function works by extracting both Skills and Competencies. For Competencies the label field
  /// is the corresponding Competency name for the CompetencyId. For Skills, we find out the Skill's
  /// associated Competency and set the label to that name against the SkillId.
  ///
  /// To handle complex display options involving gaps (see comment on CategoryControl), we also include
  /// gap information.
  ///
  getCategoryOptions = (t: TranslateFn): CategoryOption[] => {
    /// Use Competencies (or skills) for tags - but only Competency names as labels
    const competenciesWithIds = this.getCompetenciesWithIds(t);
    const competencyNameMap: StringKeyMap<DbKey<Skill>, I18nContent> = this.getSkillsWithIds(t).reduce<
      StringKeyMap<DbKey<Skill>, I18nContent>
    >((map, next) => {
      const competencyWithId = competenciesWithIds.find(c => c.id === next.value.competencyId);
      if (competencyWithId) {
        map[next.id] = competencyWithId.value.fullName;
      }
      return map;
    }, {});
    return [
      ...this.getCompetenciesWithIds(t).map(c => ({
        label: t(c.value.fullName),
        value: c.id,
        gap: this.scoreRatioOfExpected({ kind: 'competency', competencyId: c.id })
      })),
      ...this.getSkillsWithIds(t).map(s => ({
        label: t(competencyNameMap[s.id]),
        value: s.id,
        gap: this.scoreRatioOfExpected({ kind: 'competency', competencyId: s.value.competencyId })
      }))
    ];
  };
}

/**
 * Data and actions for the (demo) quiz results screen.   TODO: Generalise to manager quiz results views
 */
export class QuizResultsStore {
  /** Response from the server */
  @observable.ref propsToLoad: MaybeLoading<LoadedProps> = { kind: 'loading' };
  // @observable rolesSelected?: string[];

  constructor(
    /** Service to interact with the server */
    private readonly service: //Pick<
      Service,
    //   ,
    //   | 'userQuizResults'
    //   | 'userQuizResultsForOtherRole'
    //   | 'queryComparisonRoles'
    //   | 'launchTrainingOfferingUrl'
    //   | 'queryWorkRoleScoreRanges'
    //   | 'getTrackedLink'
    // >,
    readonly quizId: DbKey<Quiz>,
    readonly userQuizId: DbKey<UserQuiz>,
    private readonly identity: LoggedIn,
    readonly settings?: Omit<Settings, 'filters'>
  ) // readonly rolesSelected?: string[]
  {
    this.setLoadedProps(userQuizId);
    // this.rolesSelected = settings && settings.roles ? settings.roles : undefined;
  }

  /** Load training offering data for onee user quiz */
  @action setLoadedProps = (userQuizId: DbKey<UserQuiz>) => {
    const promise = this.loadAll(userQuizId).then(this.applyFilters);
    this.propsToLoad = fromPromise(promise);
  };

  /** Makes getQuizResults request to the server */
  private async loadAll(userQuizId: DbKey<UserQuiz>): Promise<LoadedProps> {
    const roles = await this.service.queryComparisonRoles();
    const selectedRoleId = this.settings && this.settings.role ? this.settings.role : undefined;
    const { quizResults, userTrainingRecommendations } = await this.getQuizResults(
      userQuizId,
      this.identity.appClient.providerIds,
      selectedRoleId
    );
    /// FF2-433 ensure the role ranges are sorted correctly
    quizResults.quizMapAndScores.ranges.sort((a, b) =>
      a.scoreRange.range.v1 < b.scoreRange.range.v1 ? -1 : a.scoreRange.range.v1 > b.scoreRange.range.v1 ? 1 : 0
    );

    const qr = QuizResultsHelper.fromQuizResultsWithProvider(
      singleUserQuizResultsToQuizReportResults(quizResults),
      roles,
      this.identity.appClient.providerIds,
      selectedRoleId
    );

    // const userTrainingRecommendations = await this.getRecommendations(
    //   qr, 
    //   appUserId,
    //   userQuizId,
    //   selectedRoleId
    // );

    qr.debugReports(this.identity.appClient.debugReporting);

    return {
      qr,
      userTrainingRecommendations,
      filteredCategories: [],
      filteredCompetencies: [],
      filteredPriorities: []
    };
  }

  // Consolidate the two APIs into one, to avoid multiple round-trips doing the same
  // work on the server
  private readonly getQuizResults = async (
    userQuizId: DbKey<UserQuiz>,
    providers: DbKey<LearningProvider>[],
    otherRoleId?: DbKey<WorkRole>
  ): Promise<SingleUserResults> => {
    if (otherRoleId) {
      return this.service.userQuizResultsForOtherRole(
        userQuizId,
        providers,
        otherRoleId);
    }
    return this.service.userQuizResults(
      userQuizId,
      providers);
  }

  private readonly applyFilters = (loadedProps: LoadedProps): LoadedProps => {
    const { qr, userTrainingRecommendations: userQuizTrainingOfferings } = loadedProps;

    if (this.settings) {
      const filteredCategories = this.settings.categories;
      const filteredCompetencies = this.settings.competencies;
      const filteredPriorities = this.settings.priorities;
      let filteredTrainingOfferings;
      filteredTrainingOfferings = this.filterRecommendationsByCategory(userQuizTrainingOfferings, filteredCategories);
      filteredTrainingOfferings = this.filterRecommendationsByCompetency(
        filteredTrainingOfferings,
        filteredCompetencies
      );
      qr.setPriorityFilter(filteredPriorities);
      return {
        ...loadedProps,
        userTrainingRecommendations: filteredTrainingOfferings,
        filteredCategories: filteredCategories || [],
        filteredCompetencies: filteredCompetencies || [],
        filteredPriorities: filteredPriorities || []
      };
    }
    return loadedProps;
  };

  getSettings(): Omit<Settings, 'filters'> | undefined {
    return this.settings;
  }

  filterRecommendationsByCategory = (
    userTrainingRecommendations: SingleUserTrainingRecommendations,
    categories?: TrainingOfferingCategory[]
  ): SingleUserTrainingRecommendations => {
    if (categories === undefined || categories.length === 0) {
      return userTrainingRecommendations;
    }
    const { appUserId, competencySkillMap, recommendations } = userTrainingRecommendations.user;
    /// Reduce recommendations if category filter set
    const filteredRecommendations: Recommendation[] = recommendations.reduce((arr: Recommendation[], next) => {
      const offering = userTrainingRecommendations.data[next.trainingOfferingId];
      if (categories.includes(offering.category)) {
        arr.push(next);
      }
      return arr;
    }, []);
    return {
      user: { appUserId, competencySkillMap, recommendations: filteredRecommendations },
      data: userTrainingRecommendations.data
    };
  };

  filterRecommendationsByCompetency = (
    userTrainingRecommendations: SingleUserTrainingRecommendations,
    competencies?: string[]
  ): SingleUserTrainingRecommendations => {
    if (competencies === undefined || competencies.length === 0) {
      return userTrainingRecommendations;
    }
    const { appUserId, competencySkillMap, recommendations } = userTrainingRecommendations.user;
    /// Reduce CompetencySkillsMap
    const filteredCompetencySkillData: StringKeyMap<string, CompetencySkillTrainingOffering[]> = {};
    for (const k of Object.keys(competencySkillMap)) {
      if (competencies.includes(k)) {
        filteredCompetencySkillData[k] = competencySkillMap[k];
      }
    }
    // prevent duplicates when filtering recs
    const filteredRecommendationMap: StringKeyMap<string, Recommendation> = Object.values(
      filteredCompetencySkillData
    ).reduce((acc: StringKeyMap<string, Recommendation>, v) => {
      const csMapOfferings = v.map(item => item.value.trainingOfferingId);
      for (const rec of recommendations) {
        if (csMapOfferings.includes(rec.trainingOfferingId)) {
          acc[rec.trainingOfferingId] = rec;
        }
      }
      return acc;
    }, {});

    return {
      user: {
        appUserId,
        competencySkillMap,
        recommendations: Object.values(filteredRecommendationMap)
      },
      data: userTrainingRecommendations.data
    };
  };

  /// This is too hard to implement properly so just return true if categories set
  @computed
  get categoryFilterApplied(): boolean {
    return (
      this.settings !== undefined &&
      (this.settings.categories !== undefined && this.settings.categories.length !== 0) &&
      this.propsToLoad.kind === 'value' // &&
      // TODO: Check current (filtered) set of offerings against API returned set OR defaults
      // specifically the number of different categories included
    );
  }
  @computed
  get competencyFilterApplied(): boolean {
    return (
      this.settings !== undefined &&
      (this.settings.competencies !== undefined && this.settings.competencies.length !== 0) &&
      this.propsToLoad.kind === 'value' // &&
      // TODO: Check current (filtered) set of offerings against API returned set OR defaults
      // specifically the number of different competencies included
    );
  }
  @computed
  get priorityFilterApplied(): boolean {
    return (
      this.settings !== undefined &&
      (this.settings.priorities !== undefined && this.settings.priorities.length !== 0) &&
      this.propsToLoad.kind === 'value'
    );
  }

  @computed
  get guidanceAvailable(): boolean | undefined {
    return this.propsToLoad.kind === 'value' ? this.propsToLoad.value.qr.guidanceAvailable() : undefined;
  }

  @computed
  get recommendationsAvailable(): boolean | undefined {
    return this.propsToLoad.kind === 'value' ? this.propsToLoad.value.qr.recommendationsAvailable() : undefined;
  }

  // This initiates the mechanism for running a TIA ONLINE training offering using a one-time link, 
  // which ensures a TIA ONLINE user and enrolment exists and then ensures they work properly.
  // This is how it works:
  // 1. If we are not using the service (phoneHome = true), or no catalog entry has been set up, just return the actual
  //    SCORM player URL has hosted at TIA Online.
  // 2. Otherwise, the system replaces that URL with a link to the API which will enable the one-time URL (see below).
  // ------
  // That's the end of this specific method. However, the URL returned by this method has a special process when clicked/invoked:
  // ------
  // 3. When the user clicks on this link, which also goes to our backend, the backend then uses the TIA Online (Scodle) LMS service 
  //    to request a one-time link to the offering. 
  // 4. As we don't want to lose control of the host page in skillseeker, the MarkdownContent component has custom code for links 
  //    to use 'target=_blank'. To ensure this will work, the backend does not send back content, but instead sends an HTTP-303
  //    (See Other) URL to the browser telling the browser to visit the one-time link. 
  //    This special API has no headers or auth cookies etc; this should be safe because no data is leaked through the API when redirecting.
  // 5. Scodle LMS webhooks will work as for any TIA Online SCORM module, so we can track progress.
  getTrainingOfferingUrl = (
    offering: WithDbId<TrainingOffering>,
    userId: DbKey<AppUser>,
    phoneHome: boolean = true
  ) => {
    if (phoneHome && offering.value.catalogEntryId) {
      return this.service.launchTrainingOfferingUrl(offering.id, userId);
    } else {
      return offering.value.executeUrl;
    }
  };

}



// WEBPACK FOOTER //
// ./src/stores/quiz-results-store.ts